import { P2GAlert, P2GBox, P2GLink } from '@parcel2go/core-ui';
import { Fragment, useMemo } from 'react';
import { ApplierOption } from '../../../lib/helpers/ApplierOption';
import AlertWithIcon from '../../AlertWithIcon';
import { P2GText } from '@parcel2go/core-ui';

import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { ToInlineCommaList } from '../../../lib/helpers/StringHelper';
import { Tagged } from '../../Tagged';
import { onlyUnique } from '../../../lib/helpers/ArrayHelper';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import ActionCard from './ActionCard';

interface props {
  bulkOptions: ApplierOption[];
  exclude: ApplierOption[];
  onActionClick: (a: ApplierOption) => void;
  affected: (action: string) => number;
  onClose?: () => void;
}

interface actionGroup {
  key: string;
  title: string;
  actions: ApplierOption[];
}

export default function SmartSendActionList(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const groups = useMemo(() => {
    const uniqueActions = props.bulkOptions.filter(onlyUnique);
    const options = uniqueActions.filter(
      (x) => !props.exclude.includes(x) && !uniqueActions.find((f) => f === `${x}:Confirm`)
    );

    const output: actionGroup[] = [];

    const addOptions = options.filter((x) => x.indexOf(':Add') > -1);
    if (addOptions.length) {
      output.push({
        key: 'MISSING',
        title: t('ADD_MISSING_DETAILS_SUBTITLE', { defaultValue: 'Add Missing Details' }),
        actions: addOptions
      });
    }

    const changeOptions = options.filter((x) => x.indexOf(':Change') > -1);
    if (changeOptions.length > 0) {
      output.push({
        key: 'CHANGE',
        actions: changeOptions,
        title: t('CHANGE_EXISTING_DETAILS_SUBTITLE', { defaultValue: 'Change Existing Details' })
      });
    }

    const mpOptions = options.filter((x) =>
      ['MarkAsNotPackaged', 'MarkAsPackaged', 'Dispatch', 'Dispatch:Confirm', 'MarkAsDispatched'].includes(x)
    );

    if (mpOptions.length > 0) {
      output.push({
        key: 'MP',
        actions: mpOptions,
        title: t('MARKETPLACE_ACTIONS_SUBTITLE', { defaultValue: 'Marketplace Actions' })
      });
    }

    const extraOptionKeys = [];
    if (SettingsCache.settings.Features.includes('Protection')) {
      extraOptionKeys.push('Protection');
    }
    if (SettingsCache.settings.Features.includes('Extras')) {
      extraOptionKeys.push('Extras');
    }
    const extras = options.filter((x) => extraOptionKeys.includes(x));
    if (extras.length > 0) {
      output.push({ key: 'EXTRAS', actions: extras, title: ToInlineCommaList(extraOptionKeys) });
    }

    const docOptions = options.filter((x) =>
      [
        'Download:Labels',
        'Download:CSV:Sold',
        'Download:CSV:Booked',
        'Download:CSV:Orders',
        'Download:PickingList'
      ].includes(x)
    );
    if (docOptions.length > 0) {
      output.push({
        key: 'DOCS',
        actions: docOptions,
        title: t('DOCUMENT_ACTIONS_SUBTITLE', { defaultValue: 'Document Actions' })
      });
    }

    const standardOptions = options.filter((o) => !output.find((x) => x.actions.includes(o)));
    if (standardOptions.length > 0) {
      output.push({
        key: 'STANDARD',
        actions: standardOptions,
        title:
          standardOptions.length != options.length
            ? t('OTHER_ACTIONS_SUBTITLE', { defaultValue: 'Other Actions' })
            : undefined
      });
    }

    return output;
  }, [props.bulkOptions]);

  const sanitiseActionTag = (x: string) => {
    return `bulk-action-${x}`.toLowerCase().replace(':', '-');
  };

  const section = (list: ApplierOption[], title: string) =>
    list.length > 0 && (
      <SidePanelSection title={title}>
        <P2GBox display="flex" flexDirection="column" gap={1}>
          {list.map((x) => (
            <Tagged key={x} tag={sanitiseActionTag(x)}>
              <ActionCard action={x} affected={props.affected} onActionClick={props.onActionClick} />
            </Tagged>
          ))}
        </P2GBox>
      </SidePanelSection>
    );

  const fixIssues = groups.filter((x) => x.actions.some((x) => x.includes(':Add')));
  const other = groups.filter((x) => !x.actions.some((x) => x.includes(':Add')));

  if (fixIssues.length === 0 && other.length === 0) {
    return (
      <SidePanelSections>
        <AlertWithIcon>
          <P2GText>{t('NO_ACTION_ALERT', { defaultValue: 'There are no actions for your selection.' })}</P2GText>
        </AlertWithIcon>
      </SidePanelSections>
    );
  }

  return (
    <SidePanelSections>
      {fixIssues.length === 0 ? (
        <P2GAlert type="success">
          <P2GText>
            {t('ALL_ISSUES_RESOLVED', {
              defaultValue: 'All of your issues that can be resolved using bulk actions have been completed.'
            })}{' '}
            {props.onClose && (
              <P2GLink onClick={props.onClose}>{t('CLOSE_ACTIONS', { defaultValue: 'Close Actions' })}</P2GLink>
            )}
          </P2GText>
        </P2GAlert>
      ) : (
        fixIssues.map((g) => <Fragment key={g.key}>{section(g.actions, g.title)}</Fragment>)
      )}
      {other.map((g) => (
        <Fragment key={g.key}>{section(g.actions, g.title)}</Fragment>
      ))}
    </SidePanelSections>
  );
}
